import _extends from "@babel/runtime/helpers/extends";
/** @jsxRuntime classic */
/** @jsx jsx */
/** @jsxfrag */

import { lazy, Suspense, useCallback, useState } from 'react';
import { createRecommendationContextExtensions } from '@atlassiansox/cross-flow-extensions';
import { css, jsx } from '@emotion/react';
import Button from '@atlaskit/button/standard-button';
import MoreIcon from '@atlaskit/icon/glyph/more';
import { B100, N200, N20A, N30, N700, N800 } from '@atlaskit/theme/colors';
// eslint-disable-next-line @atlaskit/design-system/no-deprecated-imports
import { borderRadius } from '@atlaskit/theme/constants';
import { Journeys } from '@atlassian/app-provisioning';
import { formatStringMessage } from '../../../utils/formatMessage';
import { getExperimentCohort, getRecommendationDescription } from '../app-recommendations-list/experiment';
import { AppLogo } from './app-logo';
import { getExperimentValues } from './experiment';
const AppRecommendationMenu = /*#__PURE__*/lazy(() => import( /* webpackChunkName: "@atlaskit-internal_app-recommendation-menu" */'../app-recommendation-menu'));
const recommendationStyles = css({
  display: 'flex',
  width: '100%',
  justifyItems: 'stretch',
  justifyContent: 'flex-end',
  gap: "var(--ds-space-025, 2px)",
  height: '48px',
  alignItems: 'center'
});
const recommendationBodyStyles = css({
  borderRadius: borderRadius(),
  background: 'none',
  border: 'none',
  textAlign: 'left',
  display: 'flex',
  flexGrow: 1,
  backgroundColor: 'transparent',
  padding: `${"var(--ds-space-050, 4px)"} ${"var(--ds-space-100, 8px)"}`,
  lineHeight: '18px',
  alignItems: 'stretch',
  minWidth: 0,
  color: `var(--ds-text, ${N800})`,
  outlineColor: `var(--ds-border-focused, ${B100})`,
  ':hover': {
    cursor: 'pointer',
    backgroundColor: `var(--ds-background-neutral-subtle, ${N30})`,
    textDecoration: 'none',
    color: `var(--ds-text, ${N800})`
  },
  height: "var(--ds-space-600, 48px)"
});
const recommendationBodyWithHoverStyles = css({
  backgroundColor: `var(--ds-background-neutral-subtle-hovered, ${N20A})`,
  '&:hover': {
    backgroundColor: `var(--ds-background-neutral-hovered, ${N30})`
  },
  // override a transition that has a short, but noticeable delay
  transition: 'background 0s ease-out 0s'
});
const recommendationBodyIconStyles = css({
  marginRight: "var(--ds-space-100, 8px)",
  display: 'flex',
  alignItems: 'center'
});
const recommendationBodyContentStyles = css({
  overflow: 'hidden',
  fontWeight: 400,
  display: 'flex',
  flexDirection: 'column'
});
const titleStyles = css({
  fontSize: '14px',
  fontWeight: 400,
  marginTop: "var(--ds-space-025, 2px)",
  minWidth: 0,
  lineHeight: '16px'
});
const tagLineStyles = css({
  marginTop: "var(--ds-space-050, 4px)",
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  color: `var(--ds-text-subtlest, ${N200})`,
  fontSize: '11px',
  lineHeight: '16px'
});
const tblzSwitcherTagLineStyles = css({
  color: `var(--ds-text-subtlest, ${N700})`,
  fontSize: '12px'
});
const buttonStyles = css({
  minWidth: "var(--ds-space-600, 48px)",
  height: "var(--ds-space-600, 48px)",
  padding: 0,
  '&:focus-visible': {
    outlineOffset: 0,
    outline: 'revert',
    outlineColor: `var(--ds-border-focused, ${B100})`
  }
});
const buttonWithHoverStyles = css({
  backgroundColor: `var(--ds-background-neutral-subtle-hovered, ${N20A})`,
  '&:hover': {
    backgroundColor: `var(--ds-background-neutral-hovered, ${N30})`
  },
  // override a transition that has a short, but noticeable delay
  transition: 'background 0s ease-out 0s'
});
export const AppRecommendation = ({
  actionBus,
  name,
  description,
  journey = Journeys.DECIDE,
  productId,
  logo,
  appProvisioning,
  mutate,
  reasons,
  context,
  adControlsMenuLabel,
  adControlsMenuNotInterested,
  adControlsMenuWhyAmISeeingThis,
  recommendationReasonsModalTitle,
  recommendationReasonsModalExplanation,
  recommendationReasonsModalPrivacyPolicyLinkText,
  recommendationReasonsModalPrivacyPolicyLinkHref,
  recommendationReasonsModalAcceptButton
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const tblzSwitcherRoleBasedExperimentCohort = getExperimentCohort(context);
  const handleAppProvisioning = useCallback(async journeyOverride => {
    try {
      const completionStatus = await appProvisioning({
        sourceComponent: 'atlassian-switcher',
        sourceContext: 'AppRecommendation',
        productId,
        journey: journeyOverride !== null && journeyOverride !== void 0 ? journeyOverride : journey,
        extraAnalyticsAttributes: {
          tblzSwitcherRoleBasedExperimentCohort
        },
        extensions: context.sessionId ? createRecommendationContextExtensions({
          entityId: productId,
          recommendationSessionId: context.sessionId
        }) : undefined
      });
      if ((completionStatus === null || completionStatus === void 0 ? void 0 : completionStatus.success) === true) {
        mutate(currentConfiguration => {
          var _currentConfiguration;
          return {
            ...currentConfiguration,
            apps: (_currentConfiguration = currentConfiguration.apps) === null || _currentConfiguration === void 0 ? void 0 : _currentConfiguration.filter(app => app.productId !== productId)
          };
        });
      }
    } catch (error) {}
  }, [appProvisioning, productId, journey, context.sessionId, tblzSwitcherRoleBasedExperimentCohort, mutate]);
  const MenuTrigger = ({
    triggerRef,
    ...triggerProps
  }) => jsx(Button, _extends({
    "aria-label": formatStringMessage(adControlsMenuLabel, {
      productName: name
    }),
    onMouseEnter: () => setIsHovered && setIsHovered(true),
    onMouseLeave: () => setIsHovered && setIsHovered(false),
    appearance: "subtle"
  }, triggerProps, {
    isSelected: false // override the triggerProps value to keep a light-gray button to match existing switcher button styles
    ,
    css: [buttonStyles, isHovered && buttonWithHoverStyles],
    iconBefore: jsx(MoreIcon, {
      label: undefined,
      size: "small"
    }),
    ref: triggerRef
  }));
  const messages = {
    recommendationReasonsModalTitle,
    recommendationReasonsModalExplanation,
    recommendationReasonsModalPrivacyPolicyLinkText,
    recommendationReasonsModalPrivacyPolicyLinkHref,
    recommendationReasonsModalAcceptButton
  };
  return jsx("div", {
    css: recommendationStyles
  }, jsx("button", {
    onMouseEnter: () => setIsHovered(true),
    onMouseLeave: () => setIsHovered(false),
    onClick: () => {
      const experimentValues = getExperimentValues(context, productId);
      handleAppProvisioning(experimentValues === null || experimentValues === void 0 ? void 0 : experimentValues.journeyOverride);
      actionBus === null || actionBus === void 0 ? void 0 : actionBus.dispatch('CLICK_APP_RECOMMENDATION', {
        productId,
        action: {
          action: 'clicked',
          actionSubject: 'listItem',
          actionSubjectId: 'product'
        },
        source: 'atlassianSwitcherDiscoverProductsSection',
        skipBenefitsPageExperimentCohort: experimentValues === null || experimentValues === void 0 ? void 0 : experimentValues.cohort,
        tblzSwitcherRoleBasedExperimentCohort
      });
    },
    css: [recommendationBodyStyles, isHovered && recommendationBodyWithHoverStyles]
  }, jsx("span", {
    css: recommendationBodyIconStyles
  }, jsx(AppLogo, {
    src: logo,
    tblzSwitcherRoleBasedExperimentCohort: tblzSwitcherRoleBasedExperimentCohort
  })), jsx("span", {
    css: recommendationBodyContentStyles
  }, jsx("span", {
    css: titleStyles
  }, name), jsx("span", {
    css: [tagLineStyles, tblzSwitcherRoleBasedExperimentCohort === 'experiment' ? tblzSwitcherTagLineStyles : null]
  }, getRecommendationDescription(description, productId, tblzSwitcherRoleBasedExperimentCohort)))), jsx(Suspense, {
    fallback: jsx("div", {
      css: buttonStyles
    })
  }, jsx(AppRecommendationMenu, _extends({}, messages, {
    actionBus: actionBus,
    context: context,
    mutate: mutate,
    name: name,
    reasons: reasons,
    menu: [{
      title: adControlsMenuNotInterested,
      action: {
        actionType: 'DISMISS'
      }
    }, {
      title: adControlsMenuWhyAmISeeingThis,
      action: {
        actionType: 'REASON'
      }
    }],
    productId: productId,
    menuTrigger: MenuTrigger
  }))));
};